/**
 * Mock Execution Signal Service
 * 
 * Provides mock data for the Exo Panel until backend implementation is complete.
 * This service simulates the API defined in docs/backend/EXO_PANEL_BACKEND_REQUIREMENTS.md
 */

import {
  ExecutionSignal,
  NextSignalResponse,
  QueueStatus,
  ExoPanelStatus,
  ResolveSignalRequest,
  BlockSignalRequest,
  EscalateSignalRequest,
  ClarificationRequest,
  IExecutionSignalService,
  SignalType,
  SignalStatus
} from '../types/executionSignal';

// Mock signals for demonstration
const MOCK_SIGNALS: ExecutionSignal[] = [
  {
    id: 'signal-001',
    signal_type: 'transition_required',
    priority: 1,
    message: 'Approve invoice INV-2024-001 ($5,430) for payment processing.',
    context: {
      artifact_type: 'Invoice',
      artifact_name: 'INV-2024-001',
      artifact_id: 'artifact-inv-001',
      item_id: 'item-inv-001',
      current_state: 'pending_approval',
      target_state: 'approved'
    },
    status: 'delivered',
    available_actions: ['completed', 'blocked', 'clarification_requested', 'escalated'],
    created_at: new Date(Date.now() - 3600000).toISOString(),
    delivered_at: new Date(Date.now() - 3500000).toISOString()
  },
  {
    id: 'signal-002',
    signal_type: 'action_required',
    priority: 3,
    message: 'Complete quarterly reconciliation for Operating Account.',
    context: {
      artifact_type: 'Financial Account',
      artifact_name: 'Operating Checking',
      artifact_id: 'artifact-acct-001',
      item_id: 'item-acct-001',
      current_state: 'reconciliation_pending'
    },
    status: 'pending',
    available_actions: ['completed', 'blocked', 'clarification_requested'],
    created_at: new Date(Date.now() - 7200000).toISOString()
  },
  {
    id: 'signal-003',
    signal_type: 'approval_needed',
    priority: 2,
    message: 'Review and approve budget increase request for Marketing department.',
    context: {
      artifact_type: 'Budget Request',
      artifact_name: 'Marketing Q1 2026',
      artifact_id: 'artifact-budget-001',
      item_id: 'item-budget-001',
      current_state: 'pending_review',
      target_state: 'approved'
    },
    status: 'pending',
    available_actions: ['completed', 'blocked', 'escalated'],
    created_at: new Date(Date.now() - 1800000).toISOString()
  },
  {
    id: 'signal-004',
    signal_type: 'capacity_exceeded',
    priority: 4,
    message: 'Development capacity exceeded by 15%. Reassign or defer tasks.',
    context: {
      artifact_type: 'Resource Pool',
      artifact_name: 'Development Team',
      artifact_id: 'artifact-resource-001',
      waiting_on: ['Project Manager decision']
    },
    status: 'pending',
    available_actions: ['completed', 'blocked', 'escalated'],
    created_at: new Date(Date.now() - 86400000).toISOString()
  }
];

// Internal state for mock service
let mockSignals = [...MOCK_SIGNALS];
let currentSignalIndex = 0;

/**
 * Mock implementation of the Execution Signal Service
 */
export class MockExecutionSignalService implements IExecutionSignalService {
  private signals: ExecutionSignal[] = mockSignals;

  /**
   * Get the next signal that requires action
   */
  async getNextSignal(): Promise<NextSignalResponse> {
    // Simulate API delay
    await this.simulateDelay(100);

    const pendingSignals = this.signals
      .filter(s => s.status === 'pending' || s.status === 'delivered')
      .sort((a, b) => a.priority - b.priority);

    const nextSignal = pendingSignals[0] || null;
    
    // Mark as delivered if pending
    if (nextSignal && nextSignal.status === 'pending') {
      nextSignal.status = 'delivered';
      nextSignal.delivered_at = new Date().toISOString();
    }

    return {
      signal: nextSignal,
      queue_status: this.getQueueStatusSync()
    };
  }

  /**
   * Get all pending signals
   */
  async getPendingSignals(params?: { 
    status?: string; 
    priority_max?: number; 
    limit?: number 
  }): Promise<ExecutionSignal[]> {
    await this.simulateDelay(100);

    let filtered = this.signals.filter(s => 
      s.status === 'pending' || s.status === 'delivered'
    );

    if (params?.status) {
      const statuses = params.status.split(',') as SignalStatus[];
      filtered = filtered.filter(s => statuses.includes(s.status));
    }

    if (params?.priority_max) {
      filtered = filtered.filter(s => s.priority <= params.priority_max!);
    }

    filtered = filtered.sort((a, b) => a.priority - b.priority);

    if (params?.limit) {
      filtered = filtered.slice(0, params.limit);
    }

    return filtered;
  }

  /**
   * Get a specific signal by ID
   */
  async getSignal(signalId: string): Promise<ExecutionSignal> {
    await this.simulateDelay(50);

    const signal = this.signals.find(s => s.id === signalId);
    if (!signal) {
      throw new Error(`Signal not found: ${signalId}`);
    }
    return signal;
  }

  /**
   * Resolve a signal (mark as completed)
   */
  async resolveSignal(signalId: string, resolution: ResolveSignalRequest): Promise<void> {
    await this.simulateDelay(200);

    const signal = this.signals.find(s => s.id === signalId);
    if (!signal) {
      throw new Error(`Signal not found: ${signalId}`);
    }

    signal.status = 'resolved';
    signal.resolved_at = new Date().toISOString();
    signal.resolution_type = resolution.resolution_type;
    signal.resolution_notes = resolution.notes;

    console.log(`[MockSignalService] Signal ${signalId} resolved:`, resolution);
  }

  /**
   * Block a signal with a reason
   */
  async blockSignal(signalId: string, block: BlockSignalRequest): Promise<void> {
    await this.simulateDelay(200);

    const signal = this.signals.find(s => s.id === signalId);
    if (!signal) {
      throw new Error(`Signal not found: ${signalId}`);
    }

    signal.status = 'resolved';
    signal.resolved_at = new Date().toISOString();
    signal.resolution_type = 'blocked';
    signal.resolution_reason = block.reason;
    signal.resolution_notes = block.notes;

    console.log(`[MockSignalService] Signal ${signalId} blocked:`, block);
  }

  /**
   * Escalate a signal
   */
  async escalateSignal(signalId: string, escalation: EscalateSignalRequest): Promise<void> {
    await this.simulateDelay(200);

    const signal = this.signals.find(s => s.id === signalId);
    if (!signal) {
      throw new Error(`Signal not found: ${signalId}`);
    }

    signal.status = 'escalated';
    signal.resolved_at = new Date().toISOString();
    signal.resolution_type = 'escalated';
    signal.resolution_notes = escalation.escalation_reason;

    console.log(`[MockSignalService] Signal ${signalId} escalated:`, escalation);
  }

  /**
   * Request clarification on a signal
   */
  async requestClarification(signalId: string, clarification: ClarificationRequest): Promise<void> {
    await this.simulateDelay(200);

    const signal = this.signals.find(s => s.id === signalId);
    if (!signal) {
      throw new Error(`Signal not found: ${signalId}`);
    }

    // In real implementation, this would create a clarification request
    // For mock, we just log it
    signal.resolution_type = 'clarification_requested';
    signal.resolution_notes = clarification.question;

    console.log(`[MockSignalService] Clarification requested for ${signalId}:`, clarification);
  }

  /**
   * Get the overall Exo Panel status
   */
  async getStatus(): Promise<ExoPanelStatus> {
    await this.simulateDelay(50);

    const pendingCount = this.signals.filter(s => 
      s.status === 'pending' || s.status === 'delivered'
    ).length;

    const waitingSignals = this.signals.filter(s => 
      s.context.waiting_on && s.context.waiting_on.length > 0
    );

    if (pendingCount === 0) {
      return {
        status: 'no_action_required',
        message: 'No action required.',
        waiting_on: []
      };
    }

    if (waitingSignals.length > 0 && waitingSignals.length === pendingCount) {
      return {
        status: 'waiting_upstream',
        message: 'Waiting on upstream completion.',
        waiting_on: waitingSignals.flatMap(s => s.context.waiting_on || [])
      };
    }

    return {
      status: 'signals_pending',
      message: `${pendingCount} action${pendingCount > 1 ? 's' : ''} required.`,
      waiting_on: []
    };
  }

  /**
   * Reset mock data (for testing)
   */
  reset(): void {
    this.signals = [...MOCK_SIGNALS];
  }

  /**
   * Add a mock signal (for testing)
   */
  addMockSignal(signal: Partial<ExecutionSignal>): ExecutionSignal {
    const newSignal: ExecutionSignal = {
      id: `signal-${Date.now()}`,
      signal_type: 'action_required',
      priority: 5,
      message: 'Test signal',
      context: {},
      status: 'pending',
      available_actions: ['completed', 'blocked'],
      created_at: new Date().toISOString(),
      ...signal
    };
    this.signals.push(newSignal);
    return newSignal;
  }

  private getQueueStatusSync(): QueueStatus {
    const pending = this.signals.filter(s => 
      s.status === 'pending' || s.status === 'delivered'
    );
    
    return {
      total_pending: pending.length,
      high_priority: pending.filter(s => s.priority <= 2).length,
      blocked_count: this.signals.filter(s => s.resolution_type === 'blocked').length
    };
  }

  private simulateDelay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Singleton instance for use throughout the app
export const mockExecutionSignalService = new MockExecutionSignalService();
